import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaSpinner, FaPencilAlt, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import { SINGAPORE_BANKS } from '../../utils/constants';
import './Providers.css';

const CreateProvider = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    businessName: '',
    uen: '',
    phone: '',
    contactPerson: '',
    contactInfo: '',
    bio: '',
    address: '',
    city: '',
    state: '',
    unitNumber: '',
    zipCode: '',
    categoryId: '',
    bankAccountHolderName: '',
    bankName: '',
    bankAccountNumber: '',
    bankRoutingNumber: '',
    referralCode: '',
    idDocumentUrl: '',
    certificationDocumentUrl: '',
    insuranceDocumentUrl: '',

    // Service Details
    aboutMe: '',
    languagesSpoken: '',
    yearsOfExperience: '',
    ageGroupsComfortableWith: [],
    availableForUrgentBooking: false,
    availableOnWeekends: false,
    comfortableWithPets: false,
    preferredServiceLocation: [],
    nationality: '',
    availableFrom: '',
    confinementServicesProvided: [],
    cuisinesExperiencedIn: [],
    certificationOrQualification: '',
    consultationTypesOffered: [],
    availableForUrgentConsultation: false,
    age: '',
    roomPreference: [],
    monthlyRate: '',
    extraChargesInfo: '',
    serviceAreaIds: [],
    hourlyRate: 0,
    loyaltyPoints: 0,
    loyaltyTier: 'bronze',
    isHighRisk: false,
    internalNotes: ''
  });

  const [errors, setErrors] = useState({});

  const [serviceAreas, setServiceAreas] = useState([]);
  const [newAreaName, setNewAreaName] = useState('');
  const [isAddingArea, setIsAddingArea] = useState(false);

  const ENUMS = {
    AgeGroup: ['Infant', 'Toddler', 'Preschool', 'Older Kids'],
    PreferredServiceLocation: ['Family Home', 'Own Home', 'Accompany Me'],
    RoomPreference: ['Shared Room', 'Single Room'],
    ConfinementService: [
      'Baby Care', 'Night Care', 'Cooking Confinement Meals',
      'Traditional Confinement Meals Cooking',
      'Light Household Chores', 'Breastfeeding Support', 'Herbal Baths/Tonics'
    ],
    Cuisine: ['Chinese', 'Malay', 'Indian', 'Fusion / Modern'],
    ConsultationType: ['Home Visit', 'Virtual Consultation', 'Clinic Visit']
  };

  const CATEGORY_IDS = {
    BABYSITTERS: 5,
    CONFINEMENT_NANNIES: 8,
    DOULAS: 19,
    LACTATION_CONSULTANTS: 10,
    MASSAGE_THERAPISTS: 9,
    MORE: 20
  };

  const [uploading, setUploading] = useState({
    id: false,
    cert: false,
    insur: false
  });

  useEffect(() => {
    fetchCategories();
    fetchServiceAreas();
  }, []);

  const fetchServiceAreas = async () => {
    try {
      const response = await api.get(ENDPOINTS.SERVICE_AREAS.base);
      setServiceAreas(response.data || []);
    } catch (error) {
      console.error('Failed to fetch service areas:', error);
    }
  };

  const handleAddArea = async () => {
    if (!newAreaName.trim()) {
      toast.warning('Please enter an area name');
      return;
    }

    setIsAddingArea(true);
    try {
      const response = await api.post(ENDPOINTS.SERVICE_AREAS.base, { name: newAreaName });
      const newArea = response.data;

      toast.success(`Area "${newArea.name}" added!`);

      // Refresh list
      await fetchServiceAreas();

      // Auto-select
      setFormData(prev => ({
        ...prev,
        serviceAreaIds: [...(prev.serviceAreaIds || []), newArea.id]
      }));

      setNewAreaName('');
    } catch (error) {
      console.error('Failed to add service area:', error);
      toast.error('Failed to add area: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsAddingArea(false);
    }
  };

  const handleEditArea = async (id, currentName) => {
    const newName = window.prompt(`Rename service area "${currentName}" to:`, currentName);
    if (!newName || newName === currentName) return;

    try {
      await api.patch(`${ENDPOINTS.SERVICE_AREAS.base}/${id}`, { name: newName });
      toast.success('Area updated');
      fetchServiceAreas();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleDeleteArea = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will remove it globally.`)) return;

    try {
      await api.delete(`${ENDPOINTS.SERVICE_AREAS.base}/${id}`);
      toast.success('Area deleted');

      // Remove from selection if deleted
      setFormData(prev => ({
        ...prev,
        serviceAreaIds: prev.serviceAreaIds.filter(areaId => areaId !== id)
      }));

      fetchServiceAreas();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    }
  };
  const [submitting, setSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First Name is required';
    if (!formData.lastName) newErrors.lastName = 'Last Name is required';
    if (!formData.phone) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+[0-9]{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 11 characters (+ followed by 10 digits)';
    }
    if (!formData.categoryId) newErrors.categoryId = 'Category is required';

    // if (!formData.businessName) {
    //   newErrors.businessName = 'Business Name is required';
    // }

    // if (!formData.uen) {
    //   newErrors.uen = 'UEN is required';
    // }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Dynamic Service Details Validation
    if (formData.categoryId) {
      const catId = Number(formData.categoryId);
      if (!formData.aboutMe) newErrors.aboutMe = 'About Me / Bio is required';

      if ([5, 8, 19, 10].includes(catId)) {
        if (!formData.languagesSpoken) newErrors.languagesSpoken = 'Languages Spoken is required';
      }

      if ([5, 8].includes(catId)) {
        if (!formData.yearsOfExperience) newErrors.yearsOfExperience = 'Years of Experience is required';
      }

      if (catId === 8) {
        if (!formData.nationality) newErrors.nationality = 'Nationality is required';
        if (!formData.age) newErrors.age = 'Age is required';
      }

      // Compulsory for all caregiver categories
      if (!formData.availableFrom) {
        newErrors.availableFrom = 'Available From Date is required';
      }

      if ([19, 10].includes(catId)) {
        if (!formData.certificationOrQualification) newErrors.certificationOrQualification = 'Certification / Qualification is required';
      }
    }

    // Bank Details Validation
    if (!formData.bankAccountHolderName) newErrors.bankAccountHolderName = 'Account Holder Name is required';
    if (!formData.bankName) newErrors.bankName = 'Bank Name is required';
    if (!formData.bankAccountNumber) newErrors.bankAccountNumber = 'Account Number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const {
        aboutMe, languagesSpoken, yearsOfExperience, ageGroupsComfortableWith,
        availableForUrgentBooking, availableOnWeekends, comfortableWithPets,
        preferredServiceLocation, nationality, availableFrom,
        confinementServicesProvided, cuisinesExperiencedIn,
        certificationOrQualification, consultationTypesOffered,
        availableForUrgentConsultation,
        age, roomPreference, monthlyRate, extraChargesInfo,
        ...basicInfo
      } = formData;

      const payload = {
        ...basicInfo,
        categoryId: formData.categoryId ? Number(formData.categoryId) : undefined,
        serviceAreaIds: formData.serviceAreaIds, // Moved to root
        serviceDetails: {
          aboutMe: aboutMe || '',
          languagesSpoken,
          yearsOfExperience: yearsOfExperience ? Number(yearsOfExperience) : undefined,
          ageGroupsComfortableWith,
          availableForUrgentBooking,
          availableOnWeekends,
          comfortableWithPets,
          preferredServiceLocation: preferredServiceLocation || [],
          nationality,
          availableFrom,
          confinementServicesProvided,
          cuisinesExperiencedIn,
          certificationOrQualification,
          consultationTypesOffered,
          availableForUrgentConsultation,
          age: age ? Number(age) : undefined,
          roomPreference,
          monthlyRate: (monthlyRate !== '' && monthlyRate !== null && monthlyRate !== undefined) ? Number(monthlyRate) : undefined,
          extraChargesInfo,
        }
      };

      await api.post(ENDPOINTS.PROVIDERS.base, payload);
      toast.success('Provider created successfully!');

      setSubmitting(false);
      setTimeout(() => {
        navigate('/providers');
      }, 500);
    } catch (error) {
      console.error('Failed to create provider:', error);
      toast.error('Failed to create provider: ' + (error.response?.data?.message || error.message));
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [type]: true }));

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`${ENDPOINTS.PROVIDERS.base.replace('/providers', '')}/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const url = response.url || response.data?.url;

      if (type === 'id') setFormData(prev => ({ ...prev, idDocumentUrl: url }));
      if (type === 'cert') setFormData(prev => ({ ...prev, certificationDocumentUrl: url }));
      if (type === 'insur') setFormData(prev => ({ ...prev, insuranceDocumentUrl: url }));

      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('File upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Restrict phone field to numbers only
    // Restrict numeric fields
    // Restrict phone field to numbers and + only
    if (name === 'phone') {
      const cleanedValue = value.replace(/[^\d+]/g, '');
      const finalValue = cleanedValue.startsWith('+')
        ? '+' + cleanedValue.slice(1).replace(/\+/g, '')
        : cleanedValue.replace(/\+/g, '');

      setFormData(prev => ({
        ...prev,
        [name]: finalValue.slice(0, 15)
      }));
      return;
    }

    // Restrict bank account number to numbers only
    if (name === 'bankAccountNumber') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      return;
    }

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelect = (name, value) => {
    setFormData(prev => {
      const current = prev[name] || [];
      if (current.includes(value)) {
        return { ...prev, [name]: current.filter(item => item !== value) };
      } else {
        return { ...prev, [name]: [...current, value] };
      }
    });
  };

  const renderServiceDetails = () => {
    const catId = Number(formData.categoryId);
    if (!catId) return null;

    return (
      <div className="service-details-section mt-4">
        <h5 className="section-subtitle">Bio & Service Details ({categories.find(c => c.id === catId)?.name})</h5>

        {/* aboutMe - All categories */}
        <div className="form-group">
          <label>Biography / About Me *</label>
          <textarea
            name="aboutMe"
            value={formData.aboutMe}
            onChange={handleChange}
            className={`form-control ${errors.aboutMe ? 'is-invalid' : ''}`}
            rows="4"
            placeholder="Introduce yourself and your experience..."
            required
          />
          {errors.aboutMe && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.aboutMe}</span>}
        </div>

        {/* Category Specific Fields */}
        <div className="form-grid-2">
          {/* Languages Spoken (5, 8, 19, 10) */}
          {[5, 8, 19, 10].includes(catId) && (
            <div className="form-group">
              <label>Languages Spoken *</label>
              <input type="text" name="languagesSpoken" value={formData.languagesSpoken} onChange={handleChange} className={`form-control ${errors.languagesSpoken ? 'is-invalid' : ''}`} placeholder="e.g. English, Mandarin" required />
              {errors.languagesSpoken && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.languagesSpoken}</span>}
            </div>
          )}

          {/* Years of Experience (5, 8) */}
          {[5, 8].includes(catId) && (
            <div className="form-group">
              <label>Years of Experience *</label>
              <input type="number" name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} className={`form-control ${errors.yearsOfExperience ? 'is-invalid' : ''}`} required />
              {errors.yearsOfExperience && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.yearsOfExperience}</span>}
            </div>
          )}

          {/* Nationality (8) */}
          {catId === 8 && (
            <div className="form-group">
              <label>Nationality *</label>
              <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className={`form-control ${errors.nationality ? 'is-invalid' : ''}`} required />
              {errors.nationality && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.nationality}</span>}
            </div>
          )}

          {/* Age (8) */}
          {catId === 8 && (
            <div className="form-group">
              <label>Age *</label>
              <input type="number" name="age" value={formData.age} onChange={handleChange} className={`form-control ${errors.age ? 'is-invalid' : ''}`} required />
              {errors.age && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.age}</span>}
            </div>
          )}

          {/* Available From - Compulsory for all categories */}
          <div className="form-group">
            <label>Available From *</label>
            <input type="date" name="availableFrom" value={formData.availableFrom} onChange={handleChange} className={`form-control ${errors.availableFrom ? 'is-invalid' : ''}`} required />
            {errors.availableFrom && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.availableFrom}</span>}
          </div>

          {/* Certification (19, 10) */}
          {[19, 10].includes(catId) && (
            <div className="form-group">
              <label>Certification / Qualification *</label>
              <input type="text" name="certificationOrQualification" value={formData.certificationOrQualification} onChange={handleChange} className={`form-control ${errors.certificationOrQualification ? 'is-invalid' : ''}`} required />
              {errors.certificationOrQualification && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.certificationOrQualification}</span>}
            </div>
          )}
        </div>

        {/* Multi-selects and Toggles */}
        <div className="form-grid-2 mt-3">
          {/* Age Groups Comfortable With (5) */}
          {catId === 5 && (
            <div className="form-group">
              <label>Age Groups Comfortable With</label>
              <div className="checkbox-cloud">
                {ENUMS.AgeGroup.map(age => (
                  <label key={age} className={`cloud-item ${formData.ageGroupsComfortableWith?.includes(age) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.ageGroupsComfortableWith?.includes(age)} onChange={() => handleMultiSelect('ageGroupsComfortableWith', age)} />
                    {age}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Confinement Services Provided (8) */}
          {catId === 8 && (
            <div className="form-group">
              <label>Confinement Services Provided</label>
              <div className="checkbox-cloud">
                {ENUMS.ConfinementService.map(service => (
                  <label key={service} className={`cloud-item ${formData.confinementServicesProvided?.includes(service) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.confinementServicesProvided?.includes(service)} onChange={() => handleMultiSelect('confinementServicesProvided', service)} />
                    {service}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Cuisines (8) */}
          {catId === 8 && (
            <div className="form-group">
              <label>Cuisines Experienced In</label>
              <div className="checkbox-cloud">
                {ENUMS.Cuisine.map(cuisine => (
                  <label key={cuisine} className={`cloud-item ${formData.cuisinesExperiencedIn?.includes(cuisine) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.cuisinesExperiencedIn?.includes(cuisine)} onChange={() => handleMultiSelect('cuisinesExperiencedIn', cuisine)} />
                    {cuisine}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Consultation Types (10, 9) */}
          {[10, 9].includes(catId) && (
            <div className="form-group">
              <label>Consultation Types Offered</label>
              <div className="checkbox-cloud">
                {ENUMS.ConsultationType.filter(type => catId === 9 ? type !== 'Virtual Consultation' : true).map(type => (
                  <label key={type} className={`cloud-item ${formData.consultationTypesOffered?.includes(type) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.consultationTypesOffered?.includes(type)} onChange={() => handleMultiSelect('consultationTypesOffered', type)} />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Preferences & Availability Section */}
        <div className="section-divider mt-4">Preferences & Availability</div>

        <div className="form-grid-2 mt-3">
          {/* Babysitter Location (Multi-select) */}
          {catId === 5 && (
            <div className="form-group">
              <label>Preferred Service Location</label>
              <div className="checkbox-cloud">
                {ENUMS.PreferredServiceLocation.map(loc => (
                  <label key={loc} className={`cloud-item ${formData.preferredServiceLocation?.includes(loc) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.preferredServiceLocation?.includes(loc)} onChange={() => handleMultiSelect('preferredServiceLocation', loc)} />
                    {loc}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Nanny Room Preference (Multi-select) */}
          {catId === 8 && (
            <div className="form-group">
              <label>Room Preference</label>
              <div className="checkbox-cloud">
                {ENUMS.RoomPreference.map(room => (
                  <label key={room} className={`cloud-item ${formData.roomPreference?.includes(room) ? 'active' : ''}`}>
                    <input type="checkbox" checked={formData.roomPreference?.includes(room)} onChange={() => handleMultiSelect('roomPreference', room)} />
                    {room}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Nanny Pricing & Additional Info (8) */}
        {catId === 8 && (
          <div className="form-grid-2 mt-3">
            <div className="form-group">
              <label>Monthly Rate (30 Days) $</label>
              <input type="number" name="monthlyRate" value={formData.monthlyRate} onChange={handleChange} className="form-control" placeholder="e.g. 3800" />
            </div>
            <div className="form-group">
              <label>Additional Charges (Twins, Multiples, Extra Duties)</label>
              <textarea
                name="extraChargesInfo"
                value={formData.extraChargesInfo}
                onChange={handleChange}
                className="form-control"
                rows="2"
                placeholder="e.g. Extra $200 for twins..."
              />
            </div>
          </div>
        )}

        {/* Toggles */}
        {/* Dropdowns for Availability & Preferences */}
        <div className="form-grid-2 mt-3">
          {/* Babysitter Settings (5) */}
          {catId === 5 && (
            <>
              <div className="form-group">
                <label>Available for Urgent Booking</label>
                <select name="availableForUrgentBooking" value={formData.availableForUrgentBooking ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableForUrgentBooking: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
              <div className="form-group">
                <label>Available on Weekends</label>
                <select name="availableOnWeekends" value={formData.availableOnWeekends ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableOnWeekends: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
              <div className="form-group">
                <label>Comfortable with Pets</label>
                <select name="comfortableWithPets" value={formData.comfortableWithPets ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, comfortableWithPets: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
            </>
          )}

          {/* Nanny Settings (8) */}
          {catId === 8 && (
            <>
              <div className="form-group">
                <label>Comfortable with Pets</label>
                <select name="comfortableWithPets" value={formData.comfortableWithPets ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, comfortableWithPets: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
            </>
          )}

          {/* Professional Settings (10, 9) */}
          {[10, 9].includes(catId) && (
            <>
              <div className="form-group">
                <label>Urgent / Same Day Consultation</label>
                <select name="availableForUrgentConsultation" value={formData.availableForUrgentConsultation ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableForUrgentConsultation: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
              <div className="form-group">
                <label>Available on Weekends</label>
                <select name="availableOnWeekends" value={formData.availableOnWeekends ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableOnWeekends: e.target.value === 'true' }))} className="form-control">
                  <option value="true">YES</option>
                  <option value="false">NO</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="create-provider-page">
      <div className="page-header">
        <button onClick={() => navigate('/providers')} className="btn btn-secondary">
          <FaArrowLeft /> Back to Providers
        </button>
      </div>

      <div className="content-card">
        <h3 className="card-title">Create New Provider</h3>

        <form onSubmit={handleSubmit} className="provider-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Business Name</label>
              <input
                type="text"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className="form-control"
                placeholder="Company/Studio Name"
              />
            </div>
            <div className="form-group">
              <label>UEN (Unique Entity Number)</label>
              <input
                type="text"
                name="uen"
                value={formData.uen}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g. 123456789A"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone *</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                placeholder="e.g. +6581234567"
                maxLength="11"
                required
              />
              {errors.phone && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
            </div>
            <div className="form-group">
              <label>Category <span className="text-danger">*</span></label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="">-- Choose Primary Category --</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Service Areas - Always Visible */}
          <div className="form-group mb-4 mt-2">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ marginBottom: 0, fontWeight: '600' }}>Service Areas (Select all that apply)</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="New area name..."
                  value={newAreaName}
                  onChange={(e) => setNewAreaName(e.target.value)}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArea())}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={handleAddArea}
                  disabled={isAddingArea}
                  style={{ padding: '6px 15px' }}
                >
                  {isAddingArea ? 'Adding...' : 'Add Area'}
                </button>
              </div>
            </div>
            {serviceAreas.length > 0 ? (
              <div className="checkbox-cloud">
                {serviceAreas.map(area => (
                  <div key={area.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <label className={`cloud-item ${formData.serviceAreaIds?.includes(area.id) ? 'active' : ''}`} style={{ marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.serviceAreaIds?.includes(area.id)}
                        onChange={() => handleMultiSelect('serviceAreaIds', area.id)}
                      />
                      {area.name}
                    </label>
                    <div style={{ display: 'flex', gap: '4px', marginLeft: '2px' }}>
                      <button
                        type="button"
                        onClick={() => handleEditArea(area.id, area.name)}
                        style={{ border: 'none', background: 'none', padding: '4px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                        title="Edit Area"
                      >
                        <FaPencilAlt size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteArea(area.id, area.name)}
                        style={{ border: 'none', background: 'none', padding: '4px', cursor: 'pointer', color: '#ff4d4f', display: 'flex', alignItems: 'center' }}
                        title="Delete Area"
                      >
                        <FaTrash size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-2 border rounded bg-light text-muted">
                Loading service areas...
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Person</label>
              <input
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Contact Detail (e.g. Secondary number)</label>
              <input
                type="text"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                required
              />
              {errors.email && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Password *</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {errors.password && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.password}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Short Bio (Legacy)</label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              className="form-control"
              rows="2"
            />
          </div>

          {renderServiceDetails()}

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Unit Number</label>
              <input
                type="text"
                name="unitNumber"
                value={formData.unitNumber}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <h5 className="section-subtitle mt-4">Bank Details (Bank Account)</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Account Holder Name *</label>
              <input
                type="text"
                name="bankAccountHolderName"
                value={formData.bankAccountHolderName}
                onChange={handleChange}
                className={`form-control ${errors.bankAccountHolderName ? 'is-invalid' : ''}`}
                required
              />
              {errors.bankAccountHolderName && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.bankAccountHolderName}</span>}
            </div>
            <div className="form-group">
              <label>Bank Name *</label>
              <select
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className={`form-control ${errors.bankName ? 'is-invalid' : ''}`}
                required
              >
                <option value="">-- Select Bank --</option>
                {SINGAPORE_BANKS.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
              {errors.bankName && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.bankName}</span>}
            </div>
            <div className="form-group">
              <label>Account Number *</label>
              <input
                type="text"
                name="bankAccountNumber"
                value={formData.bankAccountNumber}
                onChange={handleChange}
                className={`form-control ${errors.bankAccountNumber ? 'is-invalid' : ''}`}
                pattern="\d*"
                placeholder="Numbers only"
                required
              />

              {errors.bankAccountNumber && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.bankAccountNumber}</span>}
            </div>
            <div className="form-group" style={{ display: 'none' }}>
              <label>Routing/Branch Code</label>
              <input
                type="text"
                name="bankRoutingNumber"
                value={formData.bankRoutingNumber}
                onChange={handleChange}
                className={`form-control ${errors.bankRoutingNumber ? 'is-invalid' : ''}`}
              />
              {errors.bankRoutingNumber && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.bankRoutingNumber}</span>}
            </div>
          </div>

          <h5 className="section-subtitle mt-4">Administrative Settings</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Hourly Rate ($)</label>
              <input
                type="number"
                step="0.01"
                name="hourlyRate"
                value={formData.hourlyRate}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Loyalty Points</label>
              <input
                type="number"
                name="loyaltyPoints"
                value={formData.loyaltyPoints}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Loyalty Tier</label>
              <select
                name="loyaltyTier"
                value={formData.loyaltyTier}
                onChange={handleChange}
                className="form-control"
              >
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
                <option value="platinum">Platinum</option>
              </select>
            </div>
            {/* <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'center', marginTop: '25px' }}>
              <label className="checkbox-label" style={{ color: '#e53e3e', fontWeight: 700 }}>
                <input
                  type="checkbox"
                  name="isHighRisk"
                  checked={formData.isHighRisk}
                  onChange={handleChange}
                />
                Mark as High Risk Account
              </label>
            </div> */}
          </div>
          <div className="form-group">
            <label>Internal Admin Notes</label>
            <textarea
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleChange}
              className="form-control"
              rows="3"
              placeholder="Private notes for team only..."
            />
          </div>

          <div className="form-group">
            <label>Referral Code (Optional)</label>
            <input
              type="text"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              className="form-control"
              placeholder="e.g. ABC1234"
            />
          </div>

          <h5 className="section-subtitle mt-4">Verification Documents</h5>
          <div className="form-row">
            {/* <div className="form-group">
              <label>ID Document (NRIC/Passport) *</label>
              <div className="file-upload-container">
                <input 
                  type="file" 
                  onChange={(e) => handleFileUpload(e, 'id')} 
                  className="form-control"
                  accept="image/*,.pdf"
                />
                {uploading.id && <span className="upload-loader">Uploading...</span>}
                {formData.idDocumentUrl && <span className="upload-success">✅ Uploaded</span>}
              </div>
            </div> */}
            <div className="form-group">
              <label>Certification (Optional)</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'cert')}
                  className="form-control"
                  accept="image/*,.pdf"
                />
                {uploading.cert && <span className="upload-loader">Uploading...</span>}
                {formData.certificationDocumentUrl && <span className="upload-success">✅ Uploaded</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Insurance Document (Optional)</label>
              <div className="file-upload-container">
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'insur')}
                  className="form-control"
                  accept="image/*,.pdf"
                />
                {uploading.insur && <span className="upload-loader">Uploading...</span>}
                {formData.insuranceDocumentUrl && <span className="upload-success">✅ Uploaded</span>}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/providers')}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              style={{ opacity: submitting ? 0.7 : 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
            >
              {submitting ? (
                <><FaSpinner className="fa-spin" /> Creating...</>
              ) : (
                <><FaSave /> Create Provider</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProvider;
